import React, { useMemo } from "react"
import { graphql } from "@apollo/client/react/hoc"
import { compose } from "ramda"
import { getMember } from "../../queries/member"
import { useCoachToolsProfilePhoto } from "../../queries/profilePhoto"
import { mergeMemberDisplayPhoto } from "../../../utils/memberPhoto"
import { useMembershipJourney } from "../../../hooks/useMembershipJourney"
import ProfileWidget, { LoadingProfile } from "./Profile"
import CompactProfileWidget, { LoadingCompactProfile } from "./CompactProfile"
import ErrorLoadingProfile from "./ErrorLoadingProfile"
import WrapWithFetch from "../WrapWithFetch"

const useMemberWithDisplayPhoto = user => {
  const coachToolsProfilePhoto = useCoachToolsProfilePhoto()
  return useMemo(
    () => mergeMemberDisplayPhoto(user, coachToolsProfilePhoto),
    [user, coachToolsProfilePhoto],
  )
}

const ProfileBriefWithCoachToolsPhoto = props => {
  const user = useMemberWithDisplayPhoto(props.user)
  const { person } = useMembershipJourney()
  const displayUser = useMemo(() => {
    if (!person?.pendingAffiliate) return user

    return { ...user, type: "PF", pendingAffiliate: true }
  }, [user, person])

  return <ProfileWidget {...props} user={displayUser} />
}

const attachQuery = compose(
  graphql(getMember, {
    props: ({ data }) => ({
      data,
      loading: data.loading,
      user: data.me,
      error: data.error,
      refetch: data.refetch,
    }),
  }),
)

export const CompactProfileBrief = attachQuery(
  ({ user, loading }) =>
    (loading && <LoadingCompactProfile />) || (
      <CompactProfileWidget user={user} />
    ),
)

const ProfileBriefContent = WrapWithFetch(
  ({ user, loading, isFetching, error, refetch, reload, vertical }) => {
    if (loading || isFetching) {
      return <LoadingProfile vertical={vertical} />
    } else if (error || !user) {
      return <ErrorLoadingProfile {...{ refetch, reload }} />
    }

    return (
      <ProfileBriefWithCoachToolsPhoto
        user={user}
        error={error}
        refetch={refetch}
        vertical={vertical}
      />
    )
  },
)

export default attachQuery(ProfileBriefContent)
